import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../utils/utils';

const buttonVariants = cva(
  // `transition` bare, not `transition-colors`. The variants below now move
  // box-shadow and transform as well as colour, and `transition-colors` covers
  // none of that — the lift and the press would have snapped. Tailwind's bare
  // `transition` is exactly the right set: colours, shadow, transform, opacity,
  // and no layout properties.
  //
  // `duration-fast` + the config's `DEFAULT: var(--ease-out)` replace the stock
  // 150ms cubic-bezier(.4,0,.2,1) this was silently inheriting.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition duration-fast focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 motion-safe:active:translate-y-px [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // INTERACTION MODEL (all variants). One rule, two tiers:
        //
        //   Tinted and transparent fills express hover through COLOUR, one step
        //   away from the page ground. Solid action fills, whose contrast with
        //   the ground is already maximal, express it through ELEVATION plus a
        //   small fill move — so hover reads as lift rather than wash.
        //
        // What this replaces: every variant hovered via an alpha of its own
        // fill (`/90`, `/80`). Composited against the real ramp that lightened
        // the fill in light and darkened it in dark — one class, opposite
        // intent per theme — and made the hover colour depend on the backdrop,
        // so a button hovered differently on a card than on the page. Worst
        // case, `bg-secondary/80` over `--ground` moved 3/255 per channel: the
        // secondary button had no visible hover state in light mode at all.
        //
        // Active returns to the REST fill: lift on hover, settle on press. The
        // 1px depression lives on the shared base class (motion-safe, so
        // reduced-motion users keep the colour change without the shift), which
        // is also what carries the press where the surface ramp has no next
        // step — dark's `--accent` and `--border` are the same value.
        //
        // Shadows are `shadow-elevation-*`, not Tailwind's stock `shadow`/
        // `shadow-sm`. Stock is a black shadow, which paints nothing on dark's
        // near-black page; the elevation tokens resolve to a tinted shadow in
        // light and a lit top edge in dark, so one class is correct in both.
        default:
          'bg-primary text-primary-foreground shadow-elevation-1 hover:bg-primary-hover hover:shadow-elevation-2 active:bg-primary active:shadow-elevation-0',
        destructive:
          'bg-destructive text-destructive-foreground shadow-elevation-1 hover:bg-destructive-hover hover:shadow-elevation-2 active:bg-destructive active:shadow-elevation-0',
        // `bg-transparent`, not shadcn's stock `bg-background`. Stock works
        // because there `--background` IS the page, so the button matches
        // whatever it sits on. This app redefines that token: globals.css
        // orders the surfaces card > ground > background and states that
        // `--background` sits BELOW the page ground on purpose, for inputs and
        // filter chips that should read as recessed. So `bg-background` painted
        // an outline button as a sunken well — two steps darker than a card in
        // light, and in dark exactly `--ground`, i.e. a hole punched through
        // the card to the page. Transparent restores the variant's actual
        // intent: a border and nothing else, inheriting whatever is behind it.
        // `hover:text-accent-foreground` dropped from outline and ghost: it was
        // a no-op. `--accent-foreground` is identical to `--foreground` in both
        // themes, and both variants already set `text-foreground`.
        outline:
          'border border-input bg-transparent text-foreground shadow-elevation-1 hover:bg-accent hover:shadow-elevation-2 active:bg-accent active:shadow-elevation-0',
        secondary:
          'bg-secondary text-secondary-foreground shadow-elevation-1 hover:bg-accent hover:shadow-elevation-2 active:bg-accent active:shadow-elevation-0',
        // Ghost hovers to `--secondary`, one rung softer than outline's
        // `--accent`. Both previously landed on `--accent`, which flattened the
        // hierarchy — the lowest-emphasis variant read as heavy as the bordered
        // one. The surface ramp's ordering makes this correct in both themes
        // without a `dark:` counterpart: secondary is the smaller step from the
        // ground either way (lighter in light, darker in dark).
        ghost: 'text-foreground hover:bg-secondary active:bg-accent',
        link: 'text-primary underline-offset-4 hover:underline active:opacity-70',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        // 44px - the WCAG touch target, and the height that matches a field
        // built with `iconFieldInputClass`. Use it on forms someone meets
        // before they have an account, and on anything touch-first. Three
        // separate auth wrappers each hand-wrote `h-11 w-full` on top of this
        // primitive before it existed.
        touch: 'h-11 rounded-md px-8',
        icon: 'h-9 w-9',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
